import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  Check,
  ChevronRight,
  Clock,
  Hash,
  Headphones,
  ListTodo,
  Loader2,
  Lock,
  MessageCircle,
  Mic,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { InterviewPublicAppBar } from './InterviewPublicAppBar';
import { isPlausiblePublicToken, fetchWithTimeout } from '@core/utils/publicToken';
import { getInterviewPublicEdgeUrl, interviewEdgeConfigErrorMessage } from '@core/utils/interviewEnv';
import { interviewEdgeUndeployedMessage, parseInterviewEdgeJson } from '@core/utils/interviewEdgeResponse';
import {
  AI_INTERVIEW_LANGUAGES,
  AI_INTERVIEW_SESSION_TYPES,
  interviewTargetMinutesForSessionType,
  sessionInterviewStepLabel,
} from './interviewPrepConstants';
import { defaultVoiceForLanguage, isKnownElevenLabsVoiceId, voicesForInterviewLanguage } from './elevenLabsVoices';

function publicInterviewHeaders(): Record<string, string> {
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  };
}

export function InterviewConfigurePage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const isPopupMode = searchParams.get('popup') === '1';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orgName, setOrgName] = useState('');
  const [jobTitle, setJobTitle] = useState<string | null>(null);
  const [language, setLanguage] = useState('en-IN');
  const [sessionType, setSessionType] = useState('screening');
  const [sessionIdDisplay, setSessionIdDisplay] = useState('');
  const [sessionStatus, setSessionStatus] = useState('');
  const [startBusy, setStartBusy] = useState(false);
  const [startErr, setStartErr] = useState('');
  const [ttsVoiceId, setTtsVoiceId] = useState(() => defaultVoiceForLanguage('en-IN'));

  const interviewEdgeUrl = getInterviewPublicEdgeUrl();

  useEffect(() => {
    const list = voicesForInterviewLanguage(language);
    setTtsVoiceId((prev) => (list.some((v) => v.id === prev) ? prev : list[0].id));
  }, [language]);

  useEffect(() => {
    if (!token || !isPlausiblePublicToken(token)) {
      setLoading(false);
      setError('Invalid interview link.');
      return;
    }

    const cfgErr = interviewEdgeConfigErrorMessage();
    if (cfgErr) {
      setLoading(false);
      setError(cfgErr);
      return;
    }
    if (!interviewEdgeUrl) {
      setLoading(false);
      setError(interviewEdgeConfigErrorMessage() ?? 'Interview API URL is not configured.');
      return;
    }

    let cancelled = false;
    fetchWithTimeout(`${interviewEdgeUrl}?token=${encodeURIComponent(token)}&mode=info`, {
      headers: publicInterviewHeaders(),
      timeoutMs: 30_000,
    })
      .then(async (r) => {
        const text = await r.text();
        const parsed = parseInterviewEdgeJson(text);
        if (!parsed.ok) {
          if (cancelled) return;
          setError(
            import.meta.env.DEV
              ? interviewEdgeUndeployedMessage(r.status)
              : 'Interview service is unavailable. Ask your admin to deploy the AI interview function.',
          );
          setLoading(false);
          return;
        }
        const data = parsed.data as {
          error?: string;
          org_name?: string;
          job_title?: string | null;
          language?: string;
          session_type?: string;
          status?: string;
          session_id_display?: string;
          tts_voice_id?: string | null;
          consent_at?: string | null;
          permissions_granted_at?: string | null;
          prefs_confirmed_at?: string | null;
        };
        if (cancelled) return;
        if (
          data.prefs_confirmed_at &&
          data.status !== 'completed' &&
          data.status !== 'cancelled'
        ) {
          window.location.replace(`/interview/${token}${isPopupMode ? '?popup=1' : ''}`);
          return;
        }
        if (r.status === 410) {
          setError(data.error ?? 'This link has expired.');
          setLoading(false);
          return;
        }
        if (!r.ok || data.error) {
          setError(data.error ?? 'Could not load this session.');
          setLoading(false);
          return;
        }
        if (data.status === 'draft') {
          setError('This interview is not available yet.');
          setLoading(false);
          return;
        }
        if (!data.consent_at || !data.permissions_granted_at) {
          setError('Complete device setup first.');
          setLoading(false);
          return;
        }
        setOrgName(data.org_name ?? '—');
        setJobTitle(data.job_title ?? null);
        const langResolved = data.language ?? 'en-IN';
        if (data.language) setLanguage(langResolved);
        if (data.session_type) setSessionType(data.session_type);
        setSessionIdDisplay(data.session_id_display ?? '—');
        setSessionStatus(data.status ?? '');
        const v = typeof data.tts_voice_id === 'string' ? data.tts_voice_id.trim() : '';
        const list = voicesForInterviewLanguage(langResolved);
        if (v && list.some((x) => x.id === v) && isKnownElevenLabsVoiceId(v)) {
          setTtsVoiceId(v);
        }
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const aborted = e instanceof Error && e.name === 'AbortError';
          const msg = e instanceof Error ? e.message : String(e);
          setError(
            aborted
              ? 'Request timed out. Check your connection and try again.'
              : import.meta.env.DEV
                ? `Network error: ${msg}. Deploy: supabase functions deploy ggj_b2b_ai_interview_public`
                : 'Network error. Check your connection and try again.',
          );
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, interviewEdgeUrl]);

  async function postBody(body: Record<string, unknown>) {
    const url = getInterviewPublicEdgeUrl();
    if (!url) {
      return { r: new Response(null, { status: 503 }), data: { error: interviewEdgeConfigErrorMessage() ?? 'Not configured' } };
    }
    const r = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { ...publicInterviewHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, ...body }),
      timeoutMs: 60_000,
    });
    const text = await r.text();
    const parsed = parseInterviewEdgeJson(text);
    const data = (parsed.ok ? parsed.data : { error: interviewEdgeUndeployedMessage(r.status) }) as {
      error?: string;
      ok?: boolean;
    };
    return { r, data };
  }

  async function onStart() {
    if (!token) return;
    setStartBusy(true);
    setStartErr('');
    try {
      const { r, data } = await postBody({
        action: 'begin',
        language,
        session_type: sessionType,
        tts_voice_id: ttsVoiceId,
      });
      if (!r.ok || data.error) {
        setStartErr(data.error ?? 'Could not save your choices.');
        return;
      }
      window.location.assign(`/interview/${token}${isPopupMode ? '?popup=1' : ''}`);
    } finally {
      setStartBusy(false);
    }
  }

  const applicationTitle = jobTitle?.trim() || `${orgName} · AI interview`;
  const configureMinutes = interviewTargetMinutesForSessionType(sessionType);
  const configureStepLabel = sessionInterviewStepLabel(sessionType);
  const roundBadge =
    AI_INTERVIEW_SESSION_TYPES.find((t) => t.value === sessionType)?.label?.toUpperCase() ?? 'INTERVIEW';

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600 dark:text-sky-400" aria-hidden />
        <p className="text-sm text-slate-600 dark:text-slate-400">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-slate-50 px-4 text-center text-slate-900 dark:bg-slate-950 dark:text-slate-100">
        <p className="text-sm text-amber-800 dark:text-amber-200/90">{error}</p>
        <Link to={`/interview/prep/${token}`} className="text-sm text-brand-700 underline dark:text-sky-400">
          Back to setup
        </Link>
      </div>
    );
  }

  if (sessionStatus === 'completed' || sessionStatus === 'cancelled') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-4 dark:bg-slate-950 dark:text-slate-200">
        <p>This interview is already closed.</p>
        <Link to={`/interview/${token}`} className="text-sm text-brand-700 underline dark:text-sky-400">
          View summary
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      <InterviewPublicAppBar
        title="Before you start"
        subtitle={applicationTitle}
        icon={<Headphones className="h-5 w-5" aria-hidden />}
      />

      <div className="mx-auto grid w-full max-w-[1400px] flex-1 grid-cols-1 items-start gap-6 px-4 py-6 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)_minmax(260px,300px)] lg:gap-8 lg:py-8">
        {startErr && (
          <div className="col-span-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-500/35 dark:bg-amber-950/25 dark:text-amber-100/95">
            {startErr}
          </div>
        )}

        {/* Left — flow progress (interview steps) */}
        <aside className="space-y-4">
          <Link
            to={`/interview/prep/${token}`}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            Adjust camera &amp; mic
          </Link>

          <div>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-violet-600 dark:text-sky-400/90">
              <ListTodo className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Interview flow
            </div>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">You’re on step 2 of 3 before the conversation.</p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div className="h-full w-2/3 rounded-full bg-violet-600 dark:bg-sky-500" />
            </div>
          </div>

          <ol className="space-y-0 overflow-hidden rounded-lg border border-slate-200 bg-white text-sm dark:border-slate-700 dark:bg-slate-900/50">
            <li className="flex items-start gap-2 border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
              <div>
                <span className="font-medium text-slate-700 dark:text-slate-300">Device &amp; permissions</span>
                <p className="text-[11px] text-slate-500 dark:text-slate-500">Camera and microphone are ready.</p>
              </div>
            </li>
            <li className="flex items-start gap-2 border-b border-slate-100 border-l-4 border-l-violet-500 bg-violet-50/80 px-3 py-2.5 dark:border-slate-800 dark:bg-violet-950/25 dark:border-l-sky-500">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-600 dark:text-sky-400" aria-hidden />
              <div>
                <span className="font-medium text-slate-900 dark:text-slate-100">Language &amp; voice</span>
                <p className="text-[11px] text-slate-600 dark:text-slate-400">Choose how you and the AI interviewer will speak.</p>
              </div>
            </li>
            <li className="flex items-start gap-2 px-3 py-2.5 text-slate-400 dark:text-slate-500">
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border border-slate-300 dark:border-slate-600" aria-hidden />
              <div>
                <span className="font-medium">Start conversation</span>
                <p className="text-[11px]">Opens the guided interview when you tap Start interview.</p>
              </div>
            </li>
          </ol>

          <div className="rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-2.5 text-[11px] leading-snug text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
            <span className="font-semibold text-slate-700 dark:text-slate-300">Role context:</span>{' '}
            <span className="text-slate-600 dark:text-slate-400">{jobTitle?.trim() || 'Your application'}</span>
            <span className="text-slate-400 dark:text-slate-500"> · {orgName}</span>
          </div>
        </aside>

        {/* Center — main form */}
        <div className="mx-auto flex w-full min-w-0 max-w-xl flex-col gap-6 lg:mx-0 lg:max-w-none">
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900/40">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-600 dark:text-sky-400/95">
            Next step
          </p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            Confirm interview details, choose your <strong className="font-medium text-slate-900 dark:text-slate-200">language</strong>, then
            pick a <strong className="font-medium text-slate-900 dark:text-slate-200">female AI voice</strong> for the interviewer. This round
            is <strong className="font-medium text-slate-900 dark:text-slate-200">{configureStepLabel}</strong> — about {configureMinutes}{' '}
            minutes.
          </p>
        </div>

        <section className="space-y-3 overflow-hidden rounded-xl border border-slate-200 border-t-4 border-t-[color:var(--ggj-logo-mark-bg,#8b80f9)] bg-white p-4 dark:border-slate-700 dark:bg-slate-900/50">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <Briefcase className="h-4 w-4 shrink-0 text-violet-600 dark:text-sky-400" aria-hidden />
            Interview details
          </div>
          <dl className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500 dark:text-slate-500">Organization</dt>
              <dd className="text-right font-medium text-slate-900 dark:text-slate-100">{orgName}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500 dark:text-slate-500">Role</dt>
              <dd className="text-right font-medium text-slate-900 dark:text-slate-100">{jobTitle?.trim() || '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500 dark:text-slate-500">Round</dt>
              <dd className="text-right font-medium text-slate-900 dark:text-slate-100">{configureStepLabel}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-500 dark:text-slate-500">Suggested duration</dt>
              <dd className="text-right font-medium text-slate-900 dark:text-slate-100">~{configureMinutes} min</dd>
            </div>
          </dl>
        </section>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">Language</label>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:ring-sky-500/40"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {AI_INTERVIEW_LANGUAGES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-500">Drives speech recognition, interviewer language, and voice list.</p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600 dark:text-slate-400">Interview round</label>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:ring-sky-500/40"
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
            >
              {AI_INTERVIEW_SESSION_TYPES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-400">
              <Mic className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-sky-400" aria-hidden />
              AI voice (female, ElevenLabs)
            </div>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:ring-sky-500/40"
              value={ttsVoiceId}
              onChange={(e) => setTtsVoiceId(e.target.value)}
            >
              {voicesForInterviewLanguage(language).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label} — {v.hint}
                </option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-500">Options refresh when you change language.</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-500">
              <Hash className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-sky-400" aria-hidden />
              Session ID
            </div>
            <p className="mt-2 font-mono text-lg font-semibold tracking-wide text-slate-900 dark:text-slate-50">{sessionIdDisplay}</p>
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-500">Share only if support asks for it.</p>
          </div>
        </div>

        <div className="flex justify-center pt-1">
          <button
            type="button"
            disabled={startBusy}
            onClick={() => void onStart()}
            className="btn-primary inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl border border-slate-300 px-6 py-2.5 text-sm font-semibold shadow-md disabled:pointer-events-none disabled:opacity-50 dark:border-slate-600"
          >
            {startBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <ChevronRight className="w-4 h-4" />}
            Start interview
          </button>
        </div>
        </div>

        {/* Right — tips */}
        <aside className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/50">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-50">Before you continue</h3>
          <ul className="mt-4 space-y-3 text-sm text-slate-700 dark:text-slate-300">
            <li className="flex gap-2">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
              <span>
                Plan about <strong className="font-medium text-slate-900 dark:text-slate-100">{configureMinutes} minutes</strong> for this
                round — length can vary slightly by type ({roundBadge.toLowerCase()}).
              </span>
            </li>
            <li className="flex gap-2">
              <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
              <span>The interviewer adapts to your answers. Ask for clarification if a question isn’t clear.</span>
            </li>
            <li className="flex gap-2">
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
              <span>Your recruiter may allow retakes if something goes wrong — use them for real issues.</span>
            </li>
            <li className="flex gap-2">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
              <span>Audio is processed for this session under your recruiter’s privacy terms.</span>
            </li>
            <li className="flex gap-2">
              <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden />
              <span>You can finish this step when you’re ready — there’s no countdown on this screen.</span>
            </li>
          </ul>
          <p className="mt-5 border-t border-slate-100 pt-4 text-[11px] leading-relaxed text-slate-500 dark:border-slate-700 dark:text-slate-500">
            Tip: pick the language you&apos;ll <em>speak</em> most comfortably in; the voice list updates when you change it.
          </p>
        </aside>
      </div>
    </div>
  );
}
